import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';
// integrated-teams/{your-team}/message-templates/{message-template}/src/placements/in-app-v2/{placement-id}/index.tsx
import { useMemo } from 'react';

import { type NextBestAction } from '@post-office/confluence-next-best-action';

import type {
	BackendPlacementMessage,
	CreateBackendPlacementMessageConfig,
} from '@post-office/message-maker-node/placements/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';

import { type MessageTemplateContext } from '../../../index';
// assertMessageTemplateContext is a validation file created by Post Office's validation library Vindicated. It will be included when you create your message template. You can find out more about vindicated here: https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/vindicated-validation-library/#vindicated:-post-office's-runtime-type-validation-library
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';

import type {
	CreatePlacementMessageConfig,
	AnyPayload,
} from '@post-office/message-template-contracts/placement-v2';
import {
	type BackendSyncMessageStage,
	type AnyBackendMessageStage,
} from '@post-office/message-maker-node/placements/types';
import { ConfluenceCloudCcpId } from '@post-office/message-template-contracts/message-template-metadata';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

import { Connect3pTools, type ThirdPartyApp } from './components/connect-3p-tools';
import type { AnimationApp, Department } from './components/connect-3p-tools-modal';
import { registeredContent } from './components/content';
import {
	ANIMATION_APP_KEYS,
	DEPARTMENT_KEYS,
} from '@atlassian/confluence-account-setup/inline-connection-screen/get-animation-svg';

const ANIMATION_APPS = new Set<ThirdPartyApp>(ANIMATION_APP_KEYS);
const DEPARTMENTS = new Set<string>(DEPARTMENT_KEYS);

const baseMessageBackend: (config: CreateBackendPlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			rerankingMetadata: { productId: ConfluenceCloudCcpId },
		}),
	},
}));

export const MessageBackend: (
	config: CreateBackendPlacementMessageConfig,
) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = (config) =>
	baseMessageBackend({
		...config,
		content: registeredContent,
	});

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			nextBestActionType,
			cohort,
			cloudId,
			userId,
			unconnectedApps,
			topApps,
			department,
			onSideNavClick,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const narrowedTopApps = useMemo(
				() => topApps.filter((app): app is AnimationApp => ANIMATION_APPS.has(app)),
				[topApps],
			);
			const narrowedDepartment = useMemo(
				() => (DEPARTMENTS.has(department) ? (department as Department) : undefined),
				[department],
			);
			// `unconnectedApps` is populated server-side by the implicit-message-creator
			// via a 3-tier cascade (team-type TAP trait → site-wide TAP trait → hardcoded
			// defaults). On total creator failure the message is suppressed entirely
			// (see implicit-message/index.ts outer catch), so this prop is always
			// non-empty in production.
			// `cloudId` and `userId` are required and used by Connect3pTools to call
			// `use3pConnectorResources` and suppress the nudge when the user is
			// already connected to enough of the recommended apps.
			return (
				<Connect3pTools
					action={nextBestActionType}
					cohort={cohort}
					cloudId={cloudId}
					userId={userId}
					unconnectedApps={unconnectedApps}
					topApps={narrowedTopApps}
					department={narrowedDepartment}
					onPrimaryActionClick={onSideNavClick}
					onDismissClick={handleOnDismissed}
				/>
			);
		},
	},
});
